import { useEffect, useRef, useState } from "react";

export default function About() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-28 md:py-36 bg-[#2B1810] relative overflow-hidden" ref={ref}>
      {/* Dot pattern background */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="dots-about" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="1.5" fill="#C9A961"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots-about)"/>
        </svg>
      </div>
      {/* Warm glow accents */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#C9A961]/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-[#B8A5A8]/5 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        {/* Section label */}
        <div className={`flex items-center gap-4 mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span
            className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            01 / About
          </span>
          <div className="flex-1 h-px bg-[#C9A961]/25" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left column */}
          <div className={`transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2
              className="font-serif text-4xl md:text-5xl text-[#FAF7F2] leading-tight mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "-0.01em" }}
            >
              Navigating Systems,<br />Building Clarity
            </h2>
            <div className="w-16 h-px bg-[#C9A961] mb-8" />

            {/* Professional calling card */}
            <div className="relative rounded-2xl overflow-hidden w-full max-w-sm border border-[#C9A961]/20 bg-[#FAF7F2]/6 p-8 shadow-[0_18px_55px_rgba(0,0,0,0.18)]">
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961] mb-8" style={{ fontFamily: "'Lato', sans-serif" }}>
                Professional Calling Card
              </p>
              <p className="font-serif text-3xl leading-tight text-[#FAF7F2] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Clear thinking for messy systems.
              </p>
              <p className="text-sm leading-relaxed text-[#FAF7F2]/65 mb-8" style={{ fontFamily: "'Lato', sans-serif" }}>
                Trade compliance, technical writing, research, and creative problem-solving — grounded in structure, curiosity, and care.
              </p>
              <div className="flex flex-wrap gap-2">
                {["Compliance", "Writing", "Systems", "Creative"].map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full border border-[#C9A961]/30 text-[10px] uppercase tracking-[0.22em] text-[#C9A961]">
                    {tag}
                  </span>
                ))}
              </div>
              <img src="/bp-bear-logo.png" alt="BP bear logo" className="absolute bottom-5 right-5 h-14 w-14 opacity-75" />
            </div>
          </div>

          {/* Right column */}
          <div className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-6" style={{ fontFamily: "'Lato', sans-serif" }}>
              <p className="text-lg leading-relaxed text-[#FAF7F2]" style={{ letterSpacing: "normal" }}>
                I&apos;m Beatrice &ldquo;Bea&rdquo; Pate — a jill of all trades with over a decade of experience navigating the intersection of logistics, trade compliance, and technology. I don&apos;t just follow processes; I understand why they exist and how to make them better.
              </p>

              <p className="text-base leading-relaxed text-[#FAF7F2]/70">
                My career has been shaped by a genuine curiosity about how complex systems work — from international trade regulations and customs compliance to IT infrastructure and data systems. I hold a Six Sigma Black Belt certification and have completed Google&apos;s IT Support Professional and IBM Data Science programs, because I believe that understanding the full picture is what separates good work from great work.
              </p>

              <p className="text-base leading-relaxed text-[#FAF7F2]/70">
                What drives me isn&apos;t just the technical challenge — it&apos;s the human element. Whether I&apos;m optimizing a supply chain process, solving a compliance puzzle, or building a tool that makes someone&apos;s day easier, I bring the same intentionality: <em className="text-[#C9A961]/80">analytical rigor paired with genuine care for people.</em>
              </p>

              <p className="text-base leading-relaxed text-[#FAF7F2]/70">
                Faith grounds me. Creativity fuels me. And a deep commitment to continuous learning keeps me moving forward — always asking the next question, always looking for a better way.
              </p>
            </div>

            {/* Traits */}
            <div className="mt-10 grid grid-cols-2 gap-4">
              {[
                { icon: "ri-brain-line", label: "Systems Thinker" },
                { icon: "ri-shield-check-line", label: "Compliance Expert" },
                { icon: "ri-lightbulb-line", label: "Creative Problem Solver" },
                { icon: "ri-heart-line", label: "Faith-Centered" },
              ].map((trait) => (
                <div key={trait.label} className="flex items-center gap-3 p-3 rounded-xl bg-[#FAF7F2]/5 border border-[#C9A961]/15 hover:border-[#C9A961]/40 transition-colors duration-300">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C9A961]/15">
                    <i className={`${trait.icon} text-[#C9A961] text-base`} />
                  </div>
                  <span className="text-[13px] text-[#FAF7F2] tracking-wide" style={{ fontFamily: "'Lato', sans-serif" }}>
                    {trait.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Credentials */}
            <div className="mt-8 pt-8 border-t border-[#C9A961]/15">
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#C9A961] mb-4" style={{ fontFamily: "'Lato', sans-serif" }}>
                Certifications &amp; Education
              </p>
              <ul className="space-y-2">
                {[
                  "Six Sigma Black Belt",
                  "Google IT Support Professional",
                  "IBM Data Science Certificate",
                  "10+ Years in Logistics &amp; Trade Compliance",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-[#FAF7F2]/60" style={{ fontFamily: "'Lato', sans-serif" }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A961] flex-shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
